/**
 * T-ADMIN-SETTINGS-CONSOLIDATION — shared admin-tool access matrix.
 *
 * Single source of truth for the admin tools surfaced in the CRM. Consumed by
 * BOTH the Admin Settings hub (`AdminSettings.tsx`) AND the SYSTEM nav section
 * (`App.tsx`) so that **visibility == access**, defined exactly once.
 *
 * Background: prior to this consolidation the SYSTEM nav statically listed every
 * admin tool to every logged-in staff user; the server 403 was the only real
 * gate. This module makes the client-side nav + hub mirror the backend route
 * gates (the matrix below). The backend gates remain the load-bearing control;
 * this is defense-in-depth + a cleaner UX (you only see what you can use).
 *
 * Roles surface: `profile.roles: string[]` from `useUserProfile()`. superAdmin
 * is a superset and sees every tool.
 *
 * Access matrix (authoritative — derived from the backend route gates):
 *
 *   | Tool             | Route                   | Visible to              |
 *   |------------------|-------------------------|-------------------------|
 *   | Pricing config   | /admin/pricing          | superAdmin, opsAdmin    |
 *   | Email templates  | /admin/email-templates  | superAdmin, opsAdmin    |
 *   | Knowledge Base   | /admin/knowledge        | superAdmin, opsAdmin    |
 *   | Alerting config  | /admin/alerting         | superAdmin, opsAdmin    |
 *   | Founder list     | /admin/founder-list     | superAdmin, opsAdmin    |
 *   | QuickBooks Online| /admin/integrations/quickbooks | superAdmin, opsAdmin |
 *   | Contractors      | /admin/contractors      | superAdmin, opsAdmin    |
 *   | Role assignments | /admin/role-assignments | superAdmin only         |
 *   | Audit log search | /admin/audit            | superAdmin only         |
 *   | Account merge    | /admin/accounts/merge   | superAdmin only         |
 *   | GDPR erasure     | /admin/accounts/gdpr-purge | superAdmin only      |
 *   | Deleted accounts | /admin/accounts/deleted | superAdmin only         |
 *
 * RIG-368 — Pricing history + Account unmerge are no longer standalone hub
 * cards; each is a tab inside its primary page (Pricing config / Merge
 * accounts). Their routes still resolve.
 */

/** Staff roles relevant to admin-tool gating. */
export type Role = 'superAdmin' | 'opsAdmin';

/** A single admin tool surfaced in the hub + nav. */
export interface AdminTool {
  /** Stable identifier. */
  id: string;
  /** Visible label (matches the tool's own page header). */
  label: string;
  /** Short one-line description rendered on the hub card. */
  description: string;
  /** Target route (react-router path). Each tool keeps its own existing page. */
  to: string;
  /** Which hub group the tool belongs to. */
  group: AdminToolGroup;
  /** Roles that may see + use the tool. superAdmin is implicitly always included. */
  roles: Role[];
}

/**
 * The four groups the hub renders, in display order (RIG-362 IA reorg).
 *
 * Ordering reflects access frequency (RIG-361): the rep/people roster
 * (Team & Commission) sits first — adding/fixing a contractor is more common
 * than republishing pricing — then sales config, then integrations, then the
 * rarely-touched destructive data tools last.
 */
export type AdminToolGroup =
  | 'Team & Commission'
  | 'Sales setup'
  | 'Integrations & Alerts'
  | 'Data & Audit';

/** Group display order for the hub. */
export const ADMIN_TOOL_GROUPS: ReadonlyArray<AdminToolGroup> = [
  'Team & Commission',
  'Sales setup',
  'Integrations & Alerts',
  'Data & Audit',
];

/**
 * The 12 admin tools. `roles` lists the NON-superAdmin roles that may see each
 * tool; superAdmin is a superset handled by `visibleAdminTools`.
 */
export const ADMIN_TOOLS: ReadonlyArray<AdminTool> = [
  // --- Team & Commission (who our reps are + how leads route + what they earn) ---
  // RIG-385 — the manual "Add sales rep" tool is retired: reps auto-provision
  // from Entra on first login. The role-assignments roster below is the
  // (read-only) surface for who's in the CRM.
  {
    id: 'admin-role-assignments',
    label: 'Role assignments',
    description: 'Browse role assignments, view history, and reconcile against Entra.',
    to: '/admin/role-assignments',
    group: 'Team & Commission',
    roles: ['superAdmin'],
  },
  {
    id: 'admin-contractors',
    label: 'Contractors',
    // QA 2026-10-01 F-06: the page has no Add and no active/inactive toggle
    // (reps are added automatically at first sign-in); say what it does do.
    description:
      "See every rep's commission profile and correct their start date, name, referrer and notes. Reps are added automatically when they first sign in.",
    to: '/admin/contractors',
    group: 'Team & Commission',
    roles: ['superAdmin', 'opsAdmin'],
  },
  {
    id: 'admin-founder-list',
    label: 'Founder list',
    description: 'View, add, edit, or disable founder-list entries.',
    to: '/admin/founder-list',
    group: 'Team & Commission',
    roles: ['superAdmin', 'opsAdmin'],
  },
  // --- Sales setup (pricing + customer-facing content) ---
  {
    id: 'admin-pricing',
    label: 'Pricing config',
    description: 'Publish a new pricing configuration version (products, tiers, fees, guards).',
    to: '/admin/pricing',
    group: 'Sales setup',
    roles: ['superAdmin', 'opsAdmin'],
  },
  // RIG-368 — Pricing history is no longer a standalone hub card; it's reachable
  // via the "History" tab inside Pricing config (the /admin/pricing/history route
  // still resolves).
  {
    id: 'admin-email-templates',
    label: 'Email templates',
    description: 'View and edit the customer-facing email templates.',
    to: '/admin/email-templates',
    group: 'Sales setup',
    roles: ['superAdmin', 'opsAdmin'],
  },
  {
    id: 'admin-knowledge',
    label: 'Knowledge Base',
    description: 'Upload documents the AI assistant references. Updates apply without a deploy.',
    to: '/admin/knowledge',
    group: 'Sales setup',
    roles: ['superAdmin', 'opsAdmin'],
  },
  // --- Integrations & Alerts ---
  {
    id: 'admin-integrations-quickbooks',
    label: 'QuickBooks Online',
    description: 'Connect, reconnect, or disconnect the QuickBooks Online accounting integration.',
    to: '/admin/integrations/quickbooks',
    group: 'Integrations & Alerts',
    roles: ['superAdmin', 'opsAdmin'],
  },
  {
    id: 'admin-alerting',
    label: 'Alerting config',
    description: 'Edit alerting thresholds and recipient routing rules.',
    to: '/admin/alerting',
    group: 'Integrations & Alerts',
    roles: ['superAdmin', 'opsAdmin'],
  },
  // --- Data & Audit (superAdmin only) ---
  {
    id: 'admin-audit',
    label: 'Audit log search',
    description: 'Search the immutable audit log by actor, entity, event type, and date.',
    to: '/admin/audit',
    group: 'Data & Audit',
    roles: ['superAdmin'],
  },
  {
    id: 'admin-account-merge',
    label: 'Merge accounts',
    description: 'Merge a duplicate account into a survivor (destructive: soft-deletes the loser).',
    to: '/admin/accounts/merge',
    group: 'Data & Audit',
    roles: ['superAdmin'],
  },
  // RIG-368 — Unmerge is no longer a standalone hub card; it's reachable via the
  // "Unmerge" tab inside Merge accounts (the /admin/accounts/unmerge route still
  // resolves).
  // QA 2026-10-01 F-42: deleted accounts had no way back from the CRM.
  {
    id: 'admin-deleted-accounts',
    label: 'Deleted accounts',
    description:
      'See deleted accounts and restore one, with the contacts, quotes, activities and tasks deleted with it.',
    to: '/admin/accounts/deleted',
    group: 'Data & Audit',
    roles: ['superAdmin'],
  },
  {
    id: 'admin-gdpr-purge',
    label: 'Erase account data (GDPR)',
    description:
      'Permanently erase all data for an account (GDPR/CCPA right to erasure; irreversible; the audit log is kept).',
    to: '/admin/accounts/gdpr-purge',
    group: 'Data & Audit',
    roles: ['superAdmin'],
  },
];

/**
 * True if the given role set may see a tool. superAdmin is a superset and
 * passes for every tool regardless of the tool's `roles`.
 */
function roleCanSee(tool: AdminTool, roles: string[] | undefined): boolean {
  if (!roles || roles.length === 0) return false;
  if (roles.includes('superAdmin')) return true;
  return tool.roles.some((r) => roles.includes(r));
}

/**
 * Returns the admin tools the given roles may see, in declaration order.
 * superAdmin sees all 12; opsAdmin sees the 7 ops-eligible tools (Contractors +
 * Founder list + Pricing config + Email templates + Knowledge Base + QuickBooks
 * + Alerting); everyone else sees none.
 */
export function visibleAdminTools(roles: string[] | undefined): AdminTool[] {
  return ADMIN_TOOLS.filter((tool) => roleCanSee(tool, roles));
}

/**
 * True if the user can see the Admin Settings hub at all — i.e. at least one
 * tool is visible. Equivalent to (superAdmin || opsAdmin), since opsAdmin sees
 * the Configuration group.
 */
export function canSeeAdminSettings(roles: string[] | undefined): boolean {
  return visibleAdminTools(roles).length > 0;
}
